import { pgTable, text, serial, integer, boolean, timestamp, json, varchar, index, primaryKey, uniqueIndex } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { relations } from "drizzle-orm";
import { eq } from "drizzle-orm";

// Event and performer type definitions
export const eventTypes = ["jam", "openmic", "generic"] as const;
export type EventType = typeof eventTypes[number];

export const performerTypes = ["solo", "band"] as const;
export type PerformerType = typeof performerTypes[number];

export const userRoles = ["owner", "operator", "god"] as const;
export type UserRole = typeof userRoles[number];

export const organisationUserRoles = ["owner", "operator", "member"] as const;
export type OrganisationUserRole = typeof organisationUserRoles[number];

export const instrumentOptions = [
  "vocals",
  "bass", 
  "drums",
  "keys",
  "guitar",
  "woodwind",
  "brass"
] as const;

export type Instrument = typeof instrumentOptions[number];

export const tierOptions = ["T1", "T2", "T3"] as const;
export type Tier = typeof tierOptions[number];

// Performer status definitions
export const performerStatuses = ["participant", "consideration", "selected", "on_stage", "pending_removal"] as const;
export type PerformerStatus = typeof performerStatuses[number];

// Act management type definitions
export const actTypes = ["individual", "duo", "band", "ensemble"] as const;
export type ActType = typeof actTypes[number];

export const externalEventStatuses = ["tentative", "confirmed", "cancelled", "completed"] as const;
export type ExternalEventStatus = typeof externalEventStatuses[number];

export const bookingStatuses = ["pending", "confirmed", "cancelled", "completed"] as const;
export type BookingStatus = typeof bookingStatuses[number];

export const paymentStatuses = ["pending", "paid", "partial", "overdue"] as const;
export type PaymentStatus = typeof paymentStatuses[number];

export const commissionTypes = ["percentage", "flat"] as const;
export type CommissionType = typeof commissionTypes[number];

// Database tables
export const organisations = pgTable("organisations", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  logoUrl: text("logo_url"), // Optional logo URL for the organisation
  artistManagement: boolean("artist_management").notNull().default(false), // Enable act/artist management features
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => ({
  nameIdx: uniqueIndex("organisations_name_unique_case_insensitive").on(sql`lower(${table.name})`)
}));

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
  role: text("role", { enum: userRoles }).notNull().default("operator"),
  theme: text("theme").notNull().default("default"),
  multiScreenJams: boolean("multi_screen_jams").notNull().default(false),
});

export const organisationUsers = pgTable("organisation_users", {
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: 'cascade' }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: 'cascade' }),
  role: text("role", { enum: organisationUserRoles }).notNull().default("member"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => ({
  pk: primaryKey({ columns: [table.organisationId, table.userId] }),
}));

// New participants table - tracks participants across events
export const participants = pgTable("participants", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: 'cascade' }),
  email: text("email").default(""),
  phone: text("phone").default(""),
  instagram: text("instagram").default(""),
  lookingForBand: boolean("looking_for_band").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => ({
  // Unique constraint on name per organisation
  nameOrgIdx: uniqueIndex("participants_name_organisation_unique").on(table.name, table.organisationId)
}));

export const events = pgTable("events", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull().default("jam"), // "jam" or "openmic"
  date: timestamp("date").notNull().defaultNow(),
  venue: text("venue").notNull().default(""),
  description: text("description").notNull().default(""),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: 'cascade' }),
  timeLimit: integer("time_limit"), // in minutes
  completed: boolean("completed").notNull().default(false), // Lock event from further editing
  deleted: boolean("deleted").notNull().default(false), // Soft delete flag
  signupUrl: text("signup_url"), // Unique URL for participant signup
  signupEnabled: boolean("signup_enabled").notNull().default(false), // Whether signup link is active
  signupLimit: integer("signup_limit"), // Maximum number of performers allowed to signup
  raffleEnabled: boolean("raffle_enabled").notNull().default(false), // Whether raffles are enabled for this event
  logoUrl: text("logo_url"), // Optional logo URL for the event
  feedbackUrl: text("feedback_url"), // Unique URL for event feedback
  feedbackEnabled: boolean("feedback_enabled").notNull().default(false), // Whether feedback link is active
});

// Keep jamNights as alias for backward compatibility during migration
export const jamNights = events;

// Modified performers table - now references participants and keeps event-specific data
export const performers = pgTable("performers", {
  id: serial("id").primaryKey(),
  jamNightId: integer("jam_night_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  participantId: integer("participant_id").references(() => participants.id, { onDelete: "cascade" }), // New reference to participants
  instruments: text("instruments").array().notNull().default([]),
  performerType: text("performer_type"), // "solo" or "band" for Open Mic events
  tier: text("tier"), // Tier 1, Tier 2, Tier 3 single select
  priority: boolean("priority").notNull().default(false), // Flag to mark priority participants
  status: text("status", { enum: performerStatuses }).notNull().default("participant"), // Status of performer (replaces multiple flags)
  played: boolean("played").notNull().default(false),
  performanceCount: integer("performance_count").notNull().default(0), // Number of times performed on stage
  timeOnStage: integer("time_on_stage").notNull().default(0), // in minutes
  stageStartTime: timestamp("stage_start_time"),
  currentInstruments: text("current_instruments").array(),
  replacementName: text("replacement_name"), // Name of person replacing this performer
  replacingPerformerId: integer("replacing_performer_id"), // ID of performer being replaced
  removed: boolean("removed").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0), // For drag-and-drop ordering
  notes: text("notes"), // Additional notes about the performer for this event
  attended: boolean("attended").notNull().default(false), // For generic event attendance tracking
  raffleWins: integer("raffle_wins").notNull().default(0), // Number of raffle wins for this performer
  raffleEntrances: integer("raffle_entrances").notNull().default(0), // Number of raffle entrances for this performer
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => ({
  // Unique constraint to prevent same participant being added to same event multiple times
  // Only applies to non-removed performers
  participantEventIdx: uniqueIndex("performers_participant_event_unique").on(table.participantId, table.jamNightId).where(eq(table.removed, false)),
}));

export const messages = pgTable("messages", {
  id: serial("id").primaryKey(),
  jamNightId: integer("jam_night_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  senderName: text("sender_name").notNull().default("Anonymous"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
});

// Event feedback table
export const eventFeedback = pgTable("event_feedback", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  name: text("name"), // Optional name of person providing feedback
  enjoymentRating: integer("enjoyment_rating"), // 1-5 rating
  hoursSpent: integer("hours_spent"), // Hours spent at event
  performedOnStage: text("performed_on_stage"), // "yes", "no", "didnt_perform"
  wouldAttendNext: boolean("would_attend_next"), // Required field
  feedback: text("feedback"), // Free text feedback
  submittedAt: timestamp("submitted_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
});

// Push notification subscriptions table
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }),
  organisationId: integer("organisation_id").references(() => organisations.id, { onDelete: "cascade" }),
  endpoint: text("endpoint").notNull(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
  lastUsed: timestamp("last_used").notNull().defaultNow(),
}, (table) => ({
  // Unique constraint on endpoint to prevent duplicate subscriptions
  endpointIdx: uniqueIndex("push_subscriptions_endpoint_unique").on(table.endpoint),
}));

// Venues table for tracking performance locations
export const venues = pgTable("venues", {
  id: serial("id").primaryKey(),
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  address: text("address").notNull(),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  country: text("country"),
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  website: text("website"),
  capacity: integer("capacity"),
  notes: text("notes"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
});

// External events table for act management
export const externalEvents = pgTable("external_events", {
  id: serial("id").primaryKey(),
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  venueId: integer("venue_id").references(() => venues.id, { onDelete: "restrict" }),
  venue: text("venue"), // Legacy field for migration, will be deprecated
  date: timestamp("date").notNull(),
  location: text("location"), // Legacy field for migration, will be deprecated
  budget: integer("budget"), // Budget in cents for precision
  useVenueContact: boolean("use_venue_contact").notNull().default(true), // Whether to use venue's contact or custom contact
  contactName: text("contact_name"), // Custom contact if not using venue contact
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  notes: text("notes"),
  status: text("status").notNull().default("tentative"), // tentative, confirmed, cancelled, completed
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
});

// Acts table - can have multiple participants (band members)
export const acts = pgTable("acts", {
  id: serial("id").primaryKey(),
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
  name: text("name"), // Optional custom name (e.g., "BlueShell")
  actParticipantId: integer("act_participant_id").references(() => participants.id), // If act has custom name, creates its own participant
  actType: text("act_type").notNull().default("individual"), // individual, duo, band, ensemble
  genre: text("genre").array(), // Array of genres
  minimumFee: integer("minimum_fee"), // Minimum booking fee in cents
  preferredFee: integer("preferred_fee"), // Preferred booking fee in cents
  technicalRequirements: text("technical_requirements"), // Tech rider/requirements
  bio: text("bio"), // Act biography
  website: text("website"),
  spotifyUrl: text("spotify_url"),
  youtubeUrl: text("youtube_url"),
  soundcloudUrl: text("soundcloud_url"),
  active: boolean("active").notNull().default(true),
  rating: integer("rating"), // 1-5 star rating
  notes: text("notes"), // Internal notes about the act
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
});

// Act participants junction table - links acts to their members
export const actParticipants = pgTable("act_participants", {
  id: serial("id").primaryKey(),
  actId: integer("act_id").notNull().references(() => acts.id, { onDelete: "cascade" }),
  participantId: integer("participant_id").notNull().references(() => participants.id),
  isPrimary: boolean("is_primary").notNull().default(false), // Primary member (for display if no custom name)
  role: text("role"), // Optional role (e.g., "vocalist", "guitarist")
  joinedAt: timestamp("joined_at").notNull().defaultNow(),
  leftAt: timestamp("left_at"), // For tracking lineup changes
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  // Unique constraint on act and participant
  actParticipantIdx: uniqueIndex("act_participants_act_participant_unique").on(table.actId, table.participantId),
}));

// Bookings table for act bookings
export const bookings = pgTable("bookings", {
  id: serial("id").primaryKey(),
  actId: integer("act_id").notNull().references(() => acts.id, { onDelete: "cascade" }),
  externalEventId: integer("external_event_id").references(() => externalEvents.id, { onDelete: "cascade" }),
  internalEventId: integer("internal_event_id").references(() => events.id, { onDelete: "cascade" }),
  organisationId: integer("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"), // pending, confirmed, cancelled, completed
  fee: integer("fee"), // Agreed fee in cents
  commissionType: text("commission_type"), // 'percentage' or 'flat'
  commissionValue: integer("commission_value"), // If percentage, store as basis points (e.g., 1000 = 10%), if flat, store in cents
  commissionAmount: integer("commission_amount"), // Calculated commission amount in cents
  hasDeposit: boolean("has_deposit").notNull().default(false), // Whether a deposit is required
  depositAmount: integer("deposit_amount"), // Deposit amount in cents
  depositPaid: boolean("deposit_paid").notNull().default(false), // Whether the deposit has been paid
  performanceTime: timestamp("performance_time"),
  durationMinutes: integer("duration_minutes"),
  contractSigned: boolean("contract_signed").notNull().default(false),
  paymentStatus: text("payment_status").notNull().default("pending"), // pending, paid, partial, overdue
  invoiceNumber: text("invoice_number"),
  notes: text("notes"),
  confirmationUrl: text("confirmation_url"), // Unique URL slug for act confirmation
  confirmationEnabled: boolean("confirmation_enabled").notNull().default(true), // Whether the confirmation link is active
  confirmationRespondedAt: timestamp("confirmation_responded_at"), // When the act responded
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => ({
  // Ensure only one event type is set
  eventTypeCheck: sql`CHECK ((external_event_id IS NOT NULL AND internal_event_id IS NULL) OR (external_event_id IS NULL AND internal_event_id IS NOT NULL))`,
}));

// Event notification preferences table
export const eventNotificationPreferences = pgTable("event_notification_preferences", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => ({
  // Unique constraint to prevent duplicate preferences
  userEventIdx: uniqueIndex("event_notification_preferences_user_event_unique").on(table.userId, table.eventId),
}));

// Relations
export const organisationsRelations = relations(organisations, ({ many }) => ({
  organisationUsers: many(organisationUsers),
  events: many(events),
  participants: many(participants),
  externalEvents: many(externalEvents),
  acts: many(acts),
  bookings: many(bookings),
}));

export const usersRelations = relations(users, ({ many }) => ({
  organisationUsers: many(organisationUsers),
  pushSubscriptions: many(pushSubscriptions),
  eventNotificationPreferences: many(eventNotificationPreferences),
}));

export const organisationUsersRelations = relations(organisationUsers, ({ one }) => ({
  organisation: one(organisations, {
    fields: [organisationUsers.organisationId],
    references: [organisations.id],
  }),
  user: one(users, {
    fields: [organisationUsers.userId],
    references: [users.id],
  }),
}));

export const participantsRelations = relations(participants, ({ one, many }) => ({
  organisation: one(organisations, {
    fields: [participants.organisationId],
    references: [organisations.id],
  }),
  performers: many(performers),
  actParticipants: many(actParticipants),
}));

export const eventsRelations = relations(events, ({ many, one }) => ({
  performers: many(performers),
  messages: many(messages),
  feedback: many(eventFeedback),
  organisation: one(organisations, {
    fields: [events.organisationId],
    references: [organisations.id],
  }),
  notificationPreferences: many(eventNotificationPreferences),
}));

// Keep jamNightsRelations as alias for backward compatibility
export const jamNightsRelations = eventsRelations;

export const performersRelations = relations(performers, ({ one }) => ({
  jamNight: one(events, {
    fields: [performers.jamNightId],
    references: [events.id],
  }),
  participant: one(participants, {
    fields: [performers.participantId],
    references: [participants.id],
  }),
}));

export const messagesRelations = relations(messages, ({ one }) => ({
  jamNight: one(events, {
    fields: [messages.jamNightId],
    references: [events.id],
  }),
}));

export const eventFeedbackRelations = relations(eventFeedback, ({ one }) => ({
  event: one(events, {
    fields: [eventFeedback.eventId],
    references: [events.id],
  }),
}));

export const pushSubscriptionsRelations = relations(pushSubscriptions, ({ one }) => ({
  user: one(users, {
    fields: [pushSubscriptions.userId],
    references: [users.id],
  }),
  organisation: one(organisations, {
    fields: [pushSubscriptions.organisationId],
    references: [organisations.id],
  }),
}));

export const eventNotificationPreferencesRelations = relations(eventNotificationPreferences, ({ one }) => ({
  user: one(users, {
    fields: [eventNotificationPreferences.userId],
    references: [users.id],
  }),
  event: one(events, {
    fields: [eventNotificationPreferences.eventId],
    references: [events.id],
  }),
}));

// Venues relations
export const venuesRelations = relations(venues, ({ one, many }) => ({
  organisation: one(organisations, {
    fields: [venues.organisationId],
    references: [organisations.id],
  }),
  externalEvents: many(externalEvents),
}));

// External events relations
export const externalEventsRelations = relations(externalEvents, ({ one, many }) => ({
  organisation: one(organisations, {
    fields: [externalEvents.organisationId],
    references: [organisations.id],
  }),
  venue: one(venues, {
    fields: [externalEvents.venueId],
    references: [venues.id],
  }),
  bookings: many(bookings),
}));

// Acts relations
export const actsRelations = relations(acts, ({ one, many }) => ({
  organisation: one(organisations, {
    fields: [acts.organisationId],
    references: [organisations.id],
  }),
  actParticipant: one(participants, {
    fields: [acts.actParticipantId],
    references: [participants.id],
  }),
  actParticipants: many(actParticipants),
  bookings: many(bookings),
}));

// Act participants relations
export const actParticipantsRelations = relations(actParticipants, ({ one }) => ({
  act: one(acts, {
    fields: [actParticipants.actId],
    references: [acts.id],
  }),
  participant: one(participants, {
    fields: [actParticipants.participantId],
    references: [participants.id],
  }),
}));

// Bookings relations
export const bookingsRelations = relations(bookings, ({ one }) => ({
  act: one(acts, {
    fields: [bookings.actId],
    references: [acts.id],
  }),
  externalEvent: one(externalEvents, {
    fields: [bookings.externalEventId],
    references: [externalEvents.id],
  }),
  internalEvent: one(events, {
    fields: [bookings.internalEventId],
    references: [events.id],
  }),
  organisation: one(organisations, {
    fields: [bookings.organisationId],
    references: [organisations.id],
  }),
}));

// Session table for connect-pg-simple
export const userSessions = pgTable("user_sessions", {
  sid: varchar("sid").primaryKey(),
  sess: json("sess").notNull(),
  expire: timestamp("expire", { precision: 6, withTimezone: true }).notNull(),
  username: text("username"), // For troubleshooting - stores username of session owner
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  createdBy: text("created_by").notNull().default("system"),
  updatedBy: text("updated_by").notNull().default("system"),
}, (table) => {
  return {
    expireIdx: index("IDX_user_sessions_expire").on(table.expire),
  }
});

// Schema validation
export const insertOrganisationSchema = createInsertSchema(organisations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const insertParticipantSchema = createInsertSchema(participants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateParticipantSchema = createInsertSchema(participants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

export const insertEventSchema = createInsertSchema(events).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).extend({
  type: z.enum(eventTypes),
});

export const updateEventSchema = createInsertSchema(events).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

// Keep jamNight schemas for backward compatibility
export const insertJamNightSchema = insertEventSchema;
export const updateJamNightSchema = updateEventSchema;

export const insertPerformerSchema = createInsertSchema(performers).omit({
  id: true,
  status: true,
  played: true,
  timeOnStage: true,
  stageStartTime: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).extend({
  lookingForBand: z.boolean().optional(),
  // Add participant fields for API compatibility (will be stored in participant)
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  instagram: z.string().optional(),
  contact: z.string().optional(),
});

export const updatePerformerSchema = createInsertSchema(performers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).extend({
  // Add participant fields for API compatibility (will be stored in participant)
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  instagram: z.string().optional(),
  contact: z.string().optional(),
}).partial();

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const insertEventFeedbackSchema = createInsertSchema(eventFeedback).omit({
  id: true,
  submittedAt: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).extend({
  name: z.string().optional(),
  performedOnStage: z.enum(["yes", "no", "didnt_perform"]).optional(),
  enjoymentRating: z.number().min(1).max(5).optional(),
  hoursSpent: z.number().min(0).optional(),
});

export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
  lastUsed: true,
});

export const insertEventNotificationPreferenceSchema = createInsertSchema(eventNotificationPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateEventNotificationPreferenceSchema = createInsertSchema(eventNotificationPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

// Act management schemas
export const insertVenueSchema = createInsertSchema(venues).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateVenueSchema = createInsertSchema(venues).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

export const insertExternalEventSchema = createInsertSchema(externalEvents).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateExternalEventSchema = createInsertSchema(externalEvents).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

export const insertActSchema = createInsertSchema(acts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateActSchema = createInsertSchema(acts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

export const insertActParticipantSchema = createInsertSchema(actParticipants).omit({
  id: true,
  createdAt: true,
});

export const updateActParticipantSchema = createInsertSchema(actParticipants).omit({
  id: true,
  createdAt: true,
}).partial();

export const insertBookingSchema = createInsertSchema(bookings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const updateBookingSchema = createInsertSchema(bookings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).partial();

// Types
export type InsertEvent = z.infer<typeof insertEventSchema>;
export type UpdateEvent = z.infer<typeof updateEventSchema>;
export type Event = typeof events.$inferSelect;

// Keep JamNight types for backward compatibility
export type InsertJamNight = InsertEvent;
export type UpdateJamNight = UpdateEvent;
export type JamNight = Event;
export type EventWithOrganisation = Event & { organisation: typeof organisations.$inferSelect };
export type EventWithOrganisationAndPerformerCount = EventWithOrganisation & { performerCount: number };

// Keep JamNight types as aliases for backward compatibility
export type JamNightWithOrganisation = EventWithOrganisation;
// export type EventWithOrganisationAndPerformerCount = EventWithOrganisationAndPerformerCount;

export type InsertParticipant = z.infer<typeof insertParticipantSchema>;
export type UpdateParticipant = z.infer<typeof updateParticipantSchema>;
export type Participant = typeof participants.$inferSelect;
export type InsertPerformer = z.infer<typeof insertPerformerSchema>;
export type UpdatePerformer = z.infer<typeof updatePerformerSchema>;
export type Performer = typeof performers.$inferSelect & {
  // Fields mapped from participant for API compatibility
  name: string; // Required field from participant
  email?: string;
  phone?: string;
  instagram?: string;
  contact?: string;
};
export type PerformerWithParticipant = Performer & { 
  participant?: Participant;
};
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Message = typeof messages.$inferSelect;
export type InsertEventFeedback = z.infer<typeof insertEventFeedbackSchema>;
export type EventFeedback = typeof eventFeedback.$inferSelect;
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;
export type InsertEventNotificationPreference = z.infer<typeof insertEventNotificationPreferenceSchema>;
export type UpdateEventNotificationPreference = z.infer<typeof updateEventNotificationPreferenceSchema>;
export type EventNotificationPreference = typeof eventNotificationPreferences.$inferSelect;

// User authentication schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export type InsertOrganisation = z.infer<typeof insertOrganisationSchema>;
export type Organisation = typeof organisations.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type LoginCredentials = z.infer<typeof loginSchema>;

// Act management types
export type Venue = typeof venues.$inferSelect;
export type InsertVenue = z.infer<typeof insertVenueSchema>;
export type UpdateVenue = z.infer<typeof updateVenueSchema>;
export type ExternalEvent = typeof externalEvents.$inferSelect;
export type InsertExternalEvent = z.infer<typeof insertExternalEventSchema>;
export type UpdateExternalEvent = z.infer<typeof updateExternalEventSchema>;
export type ExternalEventWithVenue = ExternalEvent & {
  venue?: Venue;
};
export type Act = typeof acts.$inferSelect;
export type InsertAct = z.infer<typeof insertActSchema>;
export type UpdateAct = z.infer<typeof updateActSchema>;
export type ActParticipant = typeof actParticipants.$inferSelect;
export type ActWithParticipants = Act & { 
  participants: (ActParticipant & { participant: Participant })[],
  actParticipant?: Participant // The act's own participant record if it has a custom name
};
export type Booking = typeof bookings.$inferSelect;
export type InsertBooking = z.infer<typeof insertBookingSchema>;
export type UpdateBooking = z.infer<typeof updateBookingSchema>;
export type BookingWithDetails = Booking & {
  act: ActWithParticipants;
  externalEvent?: ExternalEvent;
  internalEvent?: Event;
};